import React, { useState, useEffect } from 'react';
import { 
  ShoppingCart, 
  Plus, 
  Minus, 
  Star, 
  Clock, 
  Flame, 
  Zap,
  ChefHat,
  Pizza,
  Coffee,
  Utensils,
  Heart,
  Eye,
  MapPin,
  Truck,
  Shield,
  CreditCard,
  Filter,
  Search,
  X,
  ChevronRight
} from 'lucide-react';

const FoodPlatform = () => {
  const [darkMode, setDarkMode] = useState(true);
  const [cart, setCart] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('popular');
  const [favorites, setFavorites] = useState(new Set());
  const [previewItem, setPreviewItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [deliveryInfo, setDeliveryInfo] = useState({
    address: "123 Main St, Downtown",
    time: "25-35 min",
    fee: 2.99
  });

  const backgroundGradient = darkMode 
    ? 'bg-gradient-to-br from-slate-900 via-blue-900 to-emerald-900/20'
    : 'bg-gradient-to-br from-blue-50 via-emerald-50 to-white';

  const currentTheme = {
    glass: darkMode 
      ? 'bg-gray-900/30 border-gray-700/30 backdrop-blur-xl' 
      : 'bg-white/40 border-gray-200/30 backdrop-blur-xl',
    highlight: darkMode 
      ? 'bg-gray-800/50 border-gray-700/30 border backdrop-blur-sm' 
      : 'bg-white/60 border-gray-200/30 border backdrop-blur-sm',
    text: darkMode ? 'text-white' : 'text-gray-900',
    textSecondary: darkMode ? 'text-gray-300' : 'text-gray-600',
    textMuted: darkMode ? 'text-gray-400' : 'text-gray-500',
    button: darkMode 
      ? 'bg-gradient-to-r from-orange-500 via-red-500 to-pink-500 hover:from-orange-600 hover:via-red-600 hover:to-pink-600' 
      : 'bg-gradient-to-r from-orange-400 via-red-400 to-pink-400 hover:from-orange-500 hover:via-red-500 hover:to-pink-500',
    accent: darkMode ? 'text-emerald-400' : 'text-emerald-600',
    surface: darkMode ? 'bg-gray-800/20' : 'bg-white/20'
  };

  const categories = [
    { id: 'popular', name: 'Popular', icon: Flame, color: 'text-orange-400' },
    { id: 'burgers', name: 'Burgers', icon: ChefHat, color: 'text-yellow-400' },
    { id: 'pizza', name: 'Pizza', icon: Pizza, color: 'text-red-400' },
    { id: 'drinks', name: 'Drinks', icon: Coffee, color: 'text-blue-400' },
    { id: 'sides', name: 'Sides', icon: Utensils, color: 'text-green-400' }
  ];

  const menuItems = {
    popular: [
      { 
        id: 1, 
        name: 'Signature Truffle Burger', 
        price: 16.99, 
        rating: 4.9, 
        reviews: 2847,
        time: '15-20 min', 
        image: 'https://images.unsplash.com/photo-1568901346375-23c9450c58cd?w=400&h=300&fit=crop&crop=center',
        calories: 650,
        description: 'Premium beef patty with truffle aioli, arugula, and aged gruyere',
        tags: ['Premium', 'Bestseller'],
        chef: 'Chef Marco'
      },
      { 
        id: 2, 
        name: 'Artisan Margherita Pizza', 
        price: 22.99, 
        rating: 4.8, 
        reviews: 1923,
        time: '20-25 min', 
        image: 'https://images.unsplash.com/photo-1513104890138-7c749659a591?w=400&h=300&fit=crop&crop=center',
        calories: 820,
        description: 'Wood-fired with San Marzano tomatoes, buffalo mozzarella, and fresh basil',
        tags: ['Wood-fired', 'Authentic'],
        chef: 'Chef Isabella'
      },
      { 
        id: 3, 
        name: 'Nashville Hot Chicken', 
        price: 18.99, 
        rating: 4.7, 
        reviews: 1456,
        time: '12-18 min', 
        image: 'https://images.unsplash.com/photo-1606728035253-49e8a23146de?w=400&h=300&fit=crop&crop=center',
        calories: 580,
        description: 'Crispy fried chicken with Nashville hot spice blend and pickles',
        tags: ['Spicy', 'Crispy'],
        chef: 'Chef James'
      }
    ],
    burgers: [
      { 
        id: 4, 
        name: 'Classic Double Cheeseburger', 
        price: 13.99, 
        rating: 4.6, 
        reviews: 3241,
        time: '10-15 min', 
        image: 'https://images.unsplash.com/photo-1551782450-17144efb9c50?w=400&h=300&fit=crop&crop=center',
        calories: 720,
        description: 'Two beef patties, American cheese, lettuce, tomato, pickles, special sauce',
        tags: ['Classic', 'Double'],
        chef: 'Chef Mike'
      },
      { 
        id: 5, 
        name: 'BBQ Bacon Deluxe', 
        price: 17.99, 
        rating: 4.8, 
        reviews: 1876,
        time: '15-20 min', 
        image: 'https://images.unsplash.com/photo-1572802419224-296b0aeee0d9?w=400&h=300&fit=crop&crop=center',
        calories: 820,
        description: 'Angus beef, crispy bacon, BBQ sauce, onion rings, cheddar cheese',
        tags: ['BBQ', 'Premium'],
        chef: 'Chef Sarah'
      },
      { 
        id: 6, 
        name: 'Plant-Based Beyond Burger', 
        price: 15.99, 
        rating: 4.5, 
        reviews: 967,
        time: '12-18 min', 
        image: 'https://images.unsplash.com/photo-1525059696034-4967a729002e?w=400&h=300&fit=crop&crop=center',
        calories: 420,
        description: 'Beyond meat patty, vegan cheese, avocado, sprouts, tahini sauce',
        tags: ['Vegan', 'Healthy'],
        chef: 'Chef Emma'
      }
    ],
    pizza: [
      { 
        id: 7, 
        name: 'Quattro Stagioni', 
        price: 24.99, 
        rating: 4.7, 
        reviews: 1234,
        time: '18-25 min', 
        image: 'https://images.unsplash.com/photo-1574071318508-1cdbab80d002?w=400&h=300&fit=crop&crop=center',
        calories: 780,
        description: 'Four seasons pizza with artichokes, mushrooms, ham, and olives',
        tags: ['Traditional', 'Gourmet'],
        chef: 'Chef Antonio'
      },
      { 
        id: 8, 
        name: 'Spicy Pepperoni Supreme', 
        price: 21.99, 
        rating: 4.8, 
        reviews: 2156,
        time: '20-25 min', 
        image: 'https://images.unsplash.com/photo-1628840042765-356cda07504e?w=400&h=300&fit=crop&crop=center',
        calories: 850,
        description: 'Spicy pepperoni, jalapeños, red pepper flakes, mozzarella',
        tags: ['Spicy', 'Popular'],
        chef: 'Chef Luigi'
      }
    ],
    drinks: [
      { 
        id: 9, 
        name: 'Artisan Cold Brew', 
        price: 5.99, 
        rating: 4.4, 
        reviews: 892,
        time: '2-3 min', 
        image: 'https://images.unsplash.com/photo-1461023058943-07fcbe16d735?w=400&h=300&fit=crop&crop=center',
        calories: 5,
        description: 'Single-origin Ethiopian beans, cold-brewed for 12 hours',
        tags: ['Organic', 'Premium'],
        chef: 'Barista Alex'
      },
      { 
        id: 10, 
        name: 'Tropical Smoothie Bowl', 
        price: 8.99, 
        rating: 4.6, 
        reviews: 654,
        time: '3-5 min', 
        image: 'https://images.unsplash.com/photo-1546793665-c74683f339c1?w=400&h=300&fit=crop&crop=center',
        calories: 320,
        description: 'Mango, pineapple, coconut, chia seeds, granola topping',
        tags: ['Healthy', 'Fresh'],
        chef: 'Chef Maria'
      }
    ],
    sides: [
      { 
        id: 11, 
        name: 'Truffle Parmesan Fries', 
        price: 9.99, 
        rating: 4.7, 
        reviews: 1567,
        time: '8-12 min', 
        image: 'https://images.unsplash.com/photo-1573080496219-bb080dd4f877?w=400&h=300&fit=crop&crop=center',
        calories: 480,
        description: 'Hand-cut fries with truffle oil, parmesan, and fresh herbs',
        tags: ['Gourmet', 'Crispy'],
        chef: 'Chef David'
      },
      { 
        id: 12, 
        name: 'Beer-Battered Onion Rings', 
        price: 7.99, 
        rating: 4.5, 
        reviews: 1123,
        time: '6-10 min', 
        image: 'https://images.unsplash.com/photo-1639024471283-03518883512d?w=400&h=300&fit=crop&crop=center',
        calories: 380,
        description: 'Thick-cut onions in crispy beer batter with ranch dip',
        tags: ['Crispy', 'Classic'],
        chef: 'Chef Tom'
      }
    ]
  };

  const addToCart = (item) => {
    setCart(prev => {
      const existing = prev.find(cartItem => cartItem.id === item.id);
      if (existing) {
        return prev.map(cartItem =>
          cartItem.id === item.id
            ? { ...cartItem, quantity: cartItem.quantity + 1 }
            : cartItem
        );
      }
      return [...prev, { ...item, quantity: 1 }];
    });
    
    // Add subtle animation feedback
    setIsLoading(true);
    setTimeout(() => setIsLoading(false), 500);
  };

  const updateQuantity = (id, change) => {
    setCart(prev => {
      return prev.map(item => {
        if (item.id === id) {
          const newQuantity = item.quantity + change;
          return newQuantity > 0 ? { ...item, quantity: newQuantity } : null;
        }
        return item;
      }).filter(Boolean);
    });
  };

  const toggleFavorite = (id) => {
    setFavorites(prev => {
      const newFavorites = new Set(prev);
      if (newFavorites.has(id)) {
        newFavorites.delete(id);
      } else {
        newFavorites.add(id);
      }
      return newFavorites;
    });
  };

  const cartTotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  const subtotal = cartTotal;
  const tax = cartTotal * 0.08;
  const total = subtotal + tax + deliveryInfo.fee;

  const filteredItems = menuItems[selectedCategory]?.filter(item =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.description.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  return (
    <div className={`min-h-screen ${backgroundGradient} relative overflow-hidden`}>
      {/* Background Effects */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent transform -skew-y-12 translate-y-20"></div>
      <div className="absolute top-20 left-20 w-72 h-72 bg-gradient-to-br from-blue-400/10 to-emerald-400/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 right-20 w-96 h-96 bg-gradient-to-br from-purple-400/10 to-pink-400/10 rounded-full blur-3xl"></div>
      
      <div className="relative z-10 p-6">
        <div className="max-w-7xl mx-auto">
          {/* Enhanced Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center space-x-6">
              <div>
                <h1 className={`text-5xl font-bold ${currentTheme.text} mb-2 bg-gradient-to-r from-orange-400 to-pink-400 bg-clip-text text-transparent`}>
                  Gastronomy <span className="text-emerald-400">Elite</span>
                </h1>
                <p className={`${currentTheme.textSecondary} text-lg`}>Culinary excellence delivered to your door</p>
              </div>
              <div className={`${currentTheme.highlight} rounded-2xl p-4 flex items-center space-x-3`}>
                <MapPin className="w-5 h-5 text-emerald-400" />
                <div>
                  <p className={`text-sm ${currentTheme.text} font-medium`}>{deliveryInfo.address}</p>
                  <p className={`text-xs ${currentTheme.textMuted}`}>Delivery in {deliveryInfo.time}</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search dishes..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`pl-10 pr-4 py-3 rounded-2xl ${currentTheme.highlight} ${currentTheme.text} placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-400/50 w-64`}
                />
              </div>
              
              <button
                onClick={() => setDarkMode(!darkMode)}
                className={`px-6 py-3 rounded-2xl ${currentTheme.highlight} ${currentTheme.text} hover:scale-105 transform transition-all duration-300 shadow-lg`}
              >
                {darkMode ? '☀️ Light' : '🌙 Dark'}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Enhanced Menu Section */}
            <div className="lg:col-span-2">
              <div className={`p-8 rounded-3xl ${currentTheme.glass} shadow-2xl border mb-8`}>
                {/* Enhanced Categories */}
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center space-x-4 overflow-x-auto pb-2">
                    {categories.map((category) => {
                      const IconComponent = category.icon;
                      return (
                        <button
                          key={category.id}
                          onClick={() => setSelectedCategory(category.id)}
                          className={`flex items-center space-x-3 px-8 py-4 rounded-2xl whitespace-nowrap transition-all duration-500 group ${
                            selectedCategory === category.id
                              ? `${currentTheme.button} text-white shadow-2xl transform scale-110 shadow-orange-500/25`
                              : `${currentTheme.highlight} ${currentTheme.text} hover:scale-105 hover:shadow-lg`
                          }`}
                        >
                          <IconComponent className={`w-5 h-5 ${selectedCategory === category.id ? 'text-white' : category.color} group-hover:scale-110 transition-transform`} />
                          <span className="font-semibold">{category.name}</span>
                          {selectedCategory === category.id && (
                            <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Filter className="w-4 h-4 text-gray-400" />
                    <span className={`text-sm ${currentTheme.textMuted}`}>{filteredItems.length} items</span>
                  </div>
                </div>

                {/* Enhanced Menu Items Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {filteredItems.map((item) => (
                    <div
                      key={item.id}
                      className={`group relative p-6 rounded-3xl ${currentTheme.highlight} hover:scale-105 transform transition-all duration-500 cursor-pointer hover:shadow-2xl border overflow-hidden`}
                      onClick={() => setPreviewItem(item)}
                    >
                      {/* Image Container */}
                      <div className="relative mb-6 overflow-hidden rounded-2xl h-48">
                        <img 
                          src={item.image} 
                          alt={item.name}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                          onError={(e) => {
                            e.target.src = `https://via.placeholder.com/400x300/6366f1/ffffff?text=${encodeURIComponent(item.name)}`;
                          }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        
                        {/* Tags */}
                        <div className="absolute top-3 left-3 flex flex-wrap gap-2">
                          {item.tags?.map((tag, index) => (
                            <span key={index} className="px-3 py-1 bg-white/90 text-gray-800 text-xs font-semibold rounded-full backdrop-blur-sm">
                              {tag}
                            </span>
                          ))}
                        </div>
                        
                        {/* Favorite & Preview Icons */}
                        <div className="absolute top-3 right-3 flex space-x-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleFavorite(item.id);
                            }}
                            className={`p-2 rounded-full backdrop-blur-sm transition-all duration-300 ${
                              favorites.has(item.id) 
                                ? 'bg-red-500 text-white' 
                                : 'bg-white/90 text-gray-700 hover:bg-red-500 hover:text-white'
                            }`}
                          >
                            <Heart className={`w-4 h-4 ${favorites.has(item.id) ? 'fill-current' : ''}`} />
                          </button>
                          <button className="p-2 rounded-full bg-white/90 text-gray-700 hover:bg-blue-500 hover:text-white transition-all duration-300">
                            <Eye className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      
                      {/* Content */}
                      <div className="space-y-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className={`text-xl font-bold ${currentTheme.text} mb-1 group-hover:text-emerald-400 transition-colors`}>
                              {item.name}
                            </h3>
                            <p className={`text-sm ${currentTheme.textMuted} mb-2`}>by {item.chef}</p>
                          </div>
                          <div className="flex items-center space-x-1 bg-yellow-100 dark:bg-yellow-900/30 px-2 py-1 rounded-lg">
                            <Star className="w-4 h-4 text-yellow-500 fill-current" />
                            <span className="text-sm font-semibold text-yellow-600 dark:text-yellow-400">{item.rating}</span>
                            <span className={`text-xs ${currentTheme.textMuted}`}>({item.reviews})</span>
                          </div>
                        </div>
                        
                        <p className={`text-sm ${currentTheme.textSecondary} leading-relaxed`}>{item.description}</p>
                        
                        <div className="grid grid-cols-3 gap-4 py-3">
                          <div className="flex items-center space-x-2">
                            <Clock className="w-4 h-4 text-blue-400" />
                            <span className={`text-sm ${currentTheme.textSecondary}`}>{item.time}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Zap className="w-4 h-4 text-green-400" />
                            <span className={`text-sm ${currentTheme.textSecondary}`}>{item.calories}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Truck className="w-4 h-4 text-purple-400" />
                            <span className={`text-sm ${currentTheme.textSecondary}`}>Fast</span>
                          </div>
                        </div>
                        
                        <div className="flex items-center justify-between pt-4 border-t border-gray-200/20">
                          <div className="flex flex-col">
                            <span className={`text-3xl font-bold ${currentTheme.text} bg-gradient-to-r from-emerald-400 to-blue-400 bg-clip-text text-transparent`}>
                              ${item.price}
                            </span>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              addToCart(item);
                            }}
                            disabled={isLoading}
                            className={`${currentTheme.button} text-white px-8 py-3 rounded-2xl font-semibold transform transition-all duration-300 hover:scale-110 shadow-lg hover:shadow-2xl flex items-center space-x-2 ${isLoading ? 'opacity-50' : ''}`}
                          >
                            <Plus className="w-4 h-4" />
                            <span>Add to Cart</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Enhanced Cart Section */}
            <div className="lg:col-span-1">
              <div className={`p-8 rounded-3xl ${currentTheme.glass} shadow-2xl border sticky top-6`}>
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center space-x-3">
                    <div className="relative">
                      <ShoppingCart className="w-7 h-7 text-emerald-400" />
                      {cartCount > 0 && (
                        <span className="absolute -top-2 -right-2 bg-gradient-to-r from-red-500 to-pink-500 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center font-bold animate-pulse">
                          {cartCount}
                        </span>
                      )}
                    </div>
                    <div>
                      <h3 className={`text-2xl font-bold ${currentTheme.text}`}>Your Order</h3>
                      <p className={`text-sm ${currentTheme.textMuted}`}>{cartCount} items</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Shield className="w-5 h-5 text-green-400" />
                    <span className="text-xs text-green-400 font-medium">Secure</span>
                  </div>
                </div>

                {cart.length === 0 ? (
                  <div className="text-center py-16">
                    <div className="text-8xl mb-6 animate-bounce">🛒</div>
                    <h4 className={`text-xl font-semibold ${currentTheme.text} mb-2`}>Your cart is empty</h4>
                    <p className={`${currentTheme.textSecondary} mb-6`}>Discover delicious meals and add them here!</p>
                    <div className="flex items-center justify-center space-x-4 text-sm">
                      <div className="flex items-center space-x-1">
                        <Clock className="w-4 h-4 text-blue-400" />
                        <span className={currentTheme.textMuted}>Fast delivery</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Shield className="w-4 h-4 text-green-400" />
                        <span className={currentTheme.textMuted}>Secure payment</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="space-y-4 mb-8 max-h-96 overflow-y-auto">
                      {cart.map((item) => (
                        <div key={item.id} className={`p-5 rounded-2xl ${currentTheme.highlight} hover:shadow-lg transition-all duration-300`}>
                          <div className="flex items-start space-x-4">
                            <img 
                              src={item.image} 
                              alt={item.name}
                              className="w-16 h-16 rounded-xl object-cover"
                              onError={(e) => {
                                e.target.src = `https://via.placeholder.com/64x64/6366f1/ffffff?text=${item.name.charAt(0)}`;
                              }}
                            />
                            <div className="flex-1">
                              <div className="flex items-start justify-between mb-2">
                                <div>
                                  <h4 className={`font-semibold ${currentTheme.text}`}>{item.name}</h4>
                                  <p className={`text-sm ${currentTheme.textMuted}`}>{item.chef}</p>
                                </div>
                                <span className={`text-lg font-bold ${currentTheme.text}`}>
                                  ${(item.price * item.quantity).toFixed(2)}
                                </span>
                              </div>
                              
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-3">
                                  <button
                                    onClick={() => updateQuantity(item.id, -1)}
                                    className="w-8 h-8 rounded-lg bg-gradient-to-r from-red-500 to-pink-500 text-white flex items-center justify-center hover:from-red-600 hover:to-pink-600 transition-all duration-300 hover:scale-110"
                                  >
                                    <Minus className="w-4 h-4" />
                                  </button>
                                  <span className={`font-bold text-lg ${currentTheme.text} min-w-[2rem] text-center`}>
                                    {item.quantity}
                                  </span>
                                  <button
                                    onClick={() => updateQuantity(item.id, 1)}
                                    className="w-8 h-8 rounded-lg bg-gradient-to-r from-green-500 to-emerald-500 text-white flex items-center justify-center hover:from-green-600 hover:to-emerald-600 transition-all duration-300 hover:scale-110"
                                  >
                                    <Plus className="w-4 h-4" />
                                  </button>
                                </div>
                                <span className={`text-sm ${currentTheme.textMuted}`}>
                                  ${item.price} each
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Enhanced Order Summary */}
                    <div className={`border-t pt-6 space-y-4 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className={`${currentTheme.textSecondary}`}>Subtotal</span>
                          <span className={`font-semibold ${currentTheme.text}`}>${subtotal.toFixed(2)}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className={`${currentTheme.textSecondary}`}>Tax</span>
                          <span className={`font-semibold ${currentTheme.text}`}>${tax.toFixed(2)}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <Truck className="w-4 h-4 text-blue-400" />
                            <span className={`${currentTheme.textSecondary}`}>Delivery</span>
                          </div>
                          <span className={`font-semibold ${currentTheme.text}`}>${deliveryInfo.fee.toFixed(2)}</span>
                        </div>
                      </div>
                      
                      <div className={`border-t pt-4 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                        <div className="flex items-center justify-between mb-6">
                          <span className={`text-xl font-bold ${currentTheme.text}`}>Total</span>
                          <span className={`text-3xl font-bold bg-gradient-to-r from-emerald-400 to-blue-400 bg-clip-text text-transparent`}>
                            ${total.toFixed(2)}
                          </span>
                        </div>
                        
                        <button className={`w-full ${currentTheme.button} text-white py-4 rounded-2xl font-bold text-lg transform transition-all duration-300 hover:scale-105 shadow-2xl hover:shadow-3xl mb-4 flex items-center justify-center space-x-3`}>
                          <CreditCard className="w-5 h-5" />
                          <span>Proceed to Checkout</span>
                          <ChevronRight className="w-5 h-5" />
                        </button>
                        
                        <div className="grid grid-cols-2 gap-3 text-center">
                          <div className="flex items-center justify-center space-x-2 py-2">
                            <Clock className="w-4 h-4 text-emerald-400" />
                            <span className={`text-sm ${currentTheme.textSecondary}`}>{deliveryInfo.time}</span>
                          </div>
                          <div className="flex items-center justify-center space-x-2 py-2">
                            <Shield className="w-4 h-4 text-green-400" />
                            <span className={`text-sm ${currentTheme.textSecondary}`}>Secure Pay</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Item Preview Modal */}
      {previewItem && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className={`max-w-2xl w-full max-h-[90vh] overflow-y-auto rounded-3xl ${currentTheme.glass} border shadow-3xl`}>
            <div className="relative">
              <img 
                src={previewItem.image} 
                alt={previewItem.name}
                className="w-full h-80 object-cover rounded-t-3xl"
                onError={(e) => {
                  e.target.src = `https://via.placeholder.com/800x400/6366f1/ffffff?text=${encodeURIComponent(previewItem.name)}`;
                }}
              />
              <button
                onClick={() => setPreviewItem(null)}
                className="absolute top-4 right-4 w-10 h-10 bg-black/50 text-white rounded-full flex items-center justify-center hover:bg-black/70 transition-all duration-300"
              >
                <X className="w-5 h-5" />
              </button>
              
              {/* Tags overlay */}
              <div className="absolute bottom-4 left-4 flex flex-wrap gap-2">
                {previewItem.tags?.map((tag, index) => (
                  <span key={index} className="px-3 py-1 bg-black/70 text-white text-sm font-semibold rounded-full backdrop-blur-sm">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
            
            <div className="p-8">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h2 className={`text-3xl font-bold ${currentTheme.text} mb-2`}>{previewItem.name}</h2>
                  <p className={`${currentTheme.textMuted} mb-4`}>Crafted by {previewItem.chef}</p>
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-1 bg-yellow-100 dark:bg-yellow-900/30 px-3 py-1 rounded-lg">
                      <Star className="w-4 h-4 text-yellow-500 fill-current" />
                      <span className="font-semibold text-yellow-600 dark:text-yellow-400">{previewItem.rating}</span>
                      <span className={`text-sm ${currentTheme.textMuted}`}>({previewItem.reviews} reviews)</span>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className={`text-4xl font-bold bg-gradient-to-r from-emerald-400 to-blue-400 bg-clip-text text-transparent mb-2`}>
                    ${previewItem.price}
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleFavorite(previewItem.id);
                    }}
                    className={`p-3 rounded-full transition-all duration-300 ${
                      favorites.has(previewItem.id) 
                        ? 'bg-red-500 text-white' 
                        : `${currentTheme.highlight} ${currentTheme.text} hover:bg-red-500 hover:text-white`
                    }`}
                  >
                    <Heart className={`w-5 h-5 ${favorites.has(previewItem.id) ? 'fill-current' : ''}`} />
                  </button>
                </div>
              </div>
              
              <p className={`text-lg ${currentTheme.textSecondary} leading-relaxed mb-8`}>
                {previewItem.description}
              </p>
              
              <div className="grid grid-cols-3 gap-6 mb-8">
                <div className={`text-center p-4 rounded-2xl ${currentTheme.highlight}`}>
                  <Clock className="w-6 h-6 text-blue-400 mx-auto mb-2" />
                  <div className={`font-semibold ${currentTheme.text}`}>{previewItem.time}</div>
                  <div className={`text-sm ${currentTheme.textMuted}`}>Prep Time</div>
                </div>
                <div className={`text-center p-4 rounded-2xl ${currentTheme.highlight}`}>
                  <Zap className="w-6 h-6 text-green-400 mx-auto mb-2" />
                  <div className={`font-semibold ${currentTheme.text}`}>{previewItem.calories}</div>
                  <div className={`text-sm ${currentTheme.textMuted}`}>Calories</div>
                </div>
                <div className={`text-center p-4 rounded-2xl ${currentTheme.highlight}`}>
                  <ChefHat className="w-6 h-6 text-purple-400 mx-auto mb-2" />
                  <div className={`font-semibold ${currentTheme.text}`}>Premium</div>
                  <div className={`text-sm ${currentTheme.textMuted}`}>Quality</div>
                </div>
              </div>
              
              <div className="flex space-x-4">
                <button
                  onClick={() => setPreviewItem(null)}
                  className={`flex-1 py-4 rounded-2xl ${currentTheme.highlight} ${currentTheme.text} font-semibold hover:scale-105 transform transition-all duration-300`}
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    addToCart(previewItem);
                    setPreviewItem(null);
                  }}
                  className={`flex-1 ${currentTheme.button} text-white py-4 rounded-2xl font-semibold transform transition-all duration-300 hover:scale-105 shadow-lg flex items-center justify-center space-x-2`}
                >
                  <Plus className="w-5 h-5" />
                  <span>Add to Cart</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FoodPlatform;